import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormikTextField from '../../../shared/components/formik/FormikTextField'
import FormikSelectField from '../../../shared/components/formik/FormikSelectField'
import useProjectsResources from '../../../shared/resources/api/v1/admin/docker/useProjectsResources'
import { useTeam } from '../../../contexts/TeamContext'
import { useNodes } from '../../../contexts/NodesContext'
import PageBreadcrumbs from '../../../shared/components/PageBreadcrumbs'

function ProjectCreatePage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const { teamSlug } = useParams()
  const { team } = useTeam()
  const { create } = useProjectsResources(teamSlug)
  const { nodes, loading: nodesLoading } = useNodes()

  const nodeOptions = useMemo(() => {
    return nodes.map((node) => ({
      value: node.id,
      label: node.name
    }))
  }, [nodes])

  const expectedStateOptions = useMemo(() => {
    return [
      { value: 'running', label: intl.formatMessage({ id: 'dockerProjects.expectedStates.running' }) },
      { value: 'stopped', label: intl.formatMessage({ id: 'dockerProjects.expectedStates.stopped' }) }
    ]
  }, [intl])

  const validationSchema = Yup.object({
    name: Yup.string().required(intl.formatMessage({ id: 'login.error.required' })),
    expectedState: Yup.string(),
    nodeIds: Yup.array()
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      expectedState: 'running',
      nodeIds: []
    },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      const projectData = {
        project: {
          name: values.name,
          expectedState: values.expectedState,
          nodeIds: values.nodeIds
        }
      }

      create(projectData)
        .then(() => {
          navigate(`/teams/${team.slug}/docker/projects`)
        })
        .catch((err) => {
          setStatus(err.message || 'Error creating project')
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  })

  if (nodesLoading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <PageBreadcrumbs entities={{ team }} />
        <Typography variant="h4" component="h1" gutterBottom>
          <FormattedMessage id="dockerProjects.create.title" />
        </Typography>

        <Box component="form" onSubmit={formik.handleSubmit}>
          <FormikTextField
            formik={formik}
            name="name"
            label={<FormattedMessage id="dockerProjects.create.nameLabel" />}
            autoFocus
          />

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="expectedState"
              label={<FormattedMessage id="dockerProjects.create.expectedStateLabel" />}
              options={expectedStateOptions}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormikSelectField
              formik={formik}
              name="nodeIds"
              label={<FormattedMessage id="dockerProjects.create.nodesLabel" />}
              options={nodeOptions}
              multiple
            />
          </Box>

          {formik.status && (
            <Typography color="error" sx={{ mt: 2 }}>
              {formik.status}
            </Typography>
          )}

          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={formik.isSubmitting}
            sx={{ mt: 3 }}
          >
            <FormattedMessage id="dockerProjects.create.submitButton" />
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default ProjectCreatePage
